import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/data/catalog";

const NAV = [
  { label: "Home", href: "#top" },
  { label: "Services", href: "#services" },
  { label: "Platforms", href: "#pricing" },
  { label: "Packages", href: "#packages" },
  { label: "About Us", href: "#manifesto" },
  { label: "Portfolio", href: "#showreel" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: href } });
    else if (href === "#top") window.__lenis?.scrollTo(0, { duration: 1.4 });
    else scrollToSection(href);
  };
  const goHome = () => go("#top");

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 border-b border-[#1F1F1F] bg-[#050505]/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
        <button
          data-testid="header-logo"
          onClick={goHome}
          className="flex flex-col items-start leading-none"
        >
          <span className="font-display text-lg uppercase tracking-wide text-[#E11D2E]">CyberSmoke</span>
          <span className="font-display text-[11px] uppercase tracking-[0.42em] text-[#E11D2E]/80">Media</span>
        </button>

        <nav className="hidden items-center gap-8 md:flex" data-testid="header-nav">
          {NAV.map((item) => (
            <button
              key={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(item.href)}
              className="font-mono text-xs uppercase tracking-[0.18em] text-[#8A8A8A] transition-colors duration-300 hover:text-white"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-cta"
            onClick={() => go("#pricing")}
            className="hidden border border-[#E11D2E] px-5 py-2.5 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#E11D2E] hover:shadow-[0_0_24px_rgba(225,29,46,0.35)] md:block"
          >
            Get started
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-white md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#1F1F1F] bg-[#0D0D0D]/95 md:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV.map((item) => (
                <button
                  key={item.href}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(item.href)}
                  className="border-b border-[#1F1F1F] py-4 text-left font-mono text-xs uppercase tracking-[0.18em] text-[#8A8A8A] last:border-0 hover:text-white"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
